// src/components/GraveLocator.jsx
import React, { useState, useEffect } from 'react';
import { Search as SearchIcon, MapPin, Navigation, X, Play, ArrowRight, Target, Clock, Ruler } from 'lucide-react';
import './GraveLocator.css';

export default function GraveLocator({ 
  lots = [], 
  onSelect, 
  onNavigate,
  // Navigation props from MapboxMap
  isNavigating = false,
  navigationRoute = null,
  currentStep = 0,
  onNextStep = () => {},
  onStopNavigation = () => {}
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [navigationMode, setNavigationMode] = useState(false);
  const [startingLot, setStartingLot] = useState(null);
  const [destinationQuery, setDestinationQuery] = useState('');
  const [destinationResults, setDestinationResults] = useState([]);

  useEffect(() => {
    // Debug logging
    console.log('GraveLocator: lots data:', lots);
    console.log('GraveLocator: lots length:', lots?.length);
    console.log('GraveLocator: query:', query);
    
    // Show first few lots structure for debugging
    if (lots && lots.length > 0) {
      console.log('GraveLocator: First lot structure:', lots[0]);
      console.log('GraveLocator: Sample lot properties:', {
        id: lots[0].id,
        name: lots[0].name,
        row: lots[0].row,
        column: lots[0].column,
        type: lots[0].type,
        garden: lots[0].garden,
        location: lots[0].location
      });
    }
    
    if (query) {
      const q = query.toLowerCase();
      const filteredResults = lots.filter(l => {
        // Search by grave ID (e.g., "Grave 1-1", "Niche 2-3")
        const graveId = l.id?.toLowerCase() || '';
        const graveName = l.name?.toLowerCase() || '';
        
        // Search by row and column numbers
        const row = l.row?.toString() || '';
        const column = l.column?.toString() || '';
        
        // Search by location description
        const location = l.location?.toLowerCase() || '';
        
        // Search by type (grave/niche)
        const type = l.type?.toLowerCase() || '';
        
        const matches = (
          graveId.includes(q) ||
          graveName.includes(q) ||
          row.includes(q) ||
          column.includes(q) ||
          location.includes(q) ||
          type.includes(q) ||
          // Search for combinations like "1-1", "grave 1", "niche 2", etc.
          `${row}-${column}`.includes(q) ||
          `${type} ${row}`.includes(q) ||
          `${type} ${row}-${column}`.includes(q)
        );
        
        if (matches) {
          console.log('GraveLocator: Found match:', l);
        }
        
        return matches;
      }).slice(0, 5);
      
      console.log('GraveLocator: Filtered results:', filteredResults);
      setResults(filteredResults);
    } else {
      setResults([]);
    }
  }, [query, lots]);

  useEffect(() => {
    if (destinationQuery && navigationMode) {
      const q = destinationQuery.toLowerCase();
      setDestinationResults(
        lots.filter(l => {
          // Search by grave ID (e.g., "Grave 1-1", "Niche 2-3")
          const graveId = l.id?.toLowerCase() || '';
          const graveName = l.name?.toLowerCase() || '';
          
          // Search by row and column numbers
          const row = l.row?.toString() || '';
          const column = l.column?.toString() || '';
          
          // Search by location description
          const location = l.location?.toLowerCase() || '';
          
          // Search by type (grave/niche)
          const type = l.type?.toLowerCase() || '';
          
          const matches = (
            graveId.includes(q) ||
            graveName.includes(q) ||
            row.includes(q) ||
            column.includes(q) ||
            location.includes(q) ||
            type.includes(q) ||
            // Search for combinations like "1-1", "grave 1", "niche 2", etc.
            `${row}-${column}`.includes(q) ||
            `${type} ${row}`.includes(q) ||
            `${type} ${row}-${column}`.includes(q)
          );
          
          return matches && l._id !== startingLot?._id; // Exclude the starting lot from destination options
        }).slice(0, 5)
      );
    } else {
      setDestinationResults([]);
    }
  }, [destinationQuery, lots, navigationMode, startingLot]);

  const handleClear = () => {
    setQuery('');
    setResults([]);
  };

  const handleDestinationClear = () => {
    setDestinationQuery('');
    setDestinationResults([]);
  };

  const toggleNavigationMode = () => {
    setNavigationMode(!navigationMode);
    if (navigationMode) {
      // Reset navigation state when exiting navigation mode
      setStartingLot(null);
      setDestinationQuery('');
      setDestinationResults([]);
    }
  };

  const handleSetStartingLot = (lot) => {
    setStartingLot(lot);
    setQuery('');
    setResults([]);
  };

  const handleNavigateToDestination = (destinationLot) => {
    if (startingLot && onNavigate) {
      onNavigate(startingLot, destinationLot);
    }
    // Clear destination search after navigation
    setDestinationQuery('');
    setDestinationResults([]);
  };

  const resetNavigation = () => {
    setStartingLot(null);
    setDestinationQuery('');
    setDestinationResults([]);
  };

  // Get professional icon for navigation instruction
  const getNavigationIcon = (instruction, type) => {
    if (type === 'start') {
      return <Play size={16} className="nav-icon-start" />;
    } else if (type === 'turn' || type === 'follow') {
      return <ArrowRight size={16} className="nav-icon-turn" />;
    } else if (type === 'arrive') {
      return <Target size={16} className="nav-icon-arrive" />;
    }
    return <ArrowRight size={16} className="nav-icon-default" />;
  };

  // Enhanced road description based on instruction
  const getEnhancedInstruction = (instruction, type) => {
    if (type === 'start') {
      return instruction;
    } else if (type === 'turn' || type === 'follow') {
      // The instruction should already be enhanced from MapboxMap
      // Just return it as is since we've improved the generation
      return instruction;
    } else if (type === 'arrive') {
      // For arrive instructions, try to replace ID with name
      if (instruction && instruction.includes('Arrive at')) {
        // Extract the ID from the instruction
        const idMatch = instruction.match(/Arrive at (.+)/);
        if (idMatch) {
          const targetId = idMatch[1];
          // Find the lot with this ID
          const targetLot = lots.find(lot => lot.id === targetId || lot._id === targetId);
          if (targetLot && targetLot.name) {
            return `Arrive at ${targetLot.name}`;
          }
        }
      }
      return instruction;
    }
    return instruction;
  };

  // Debug: Show if no lots data (only log once)
  useEffect(() => {
    if (!lots || lots.length === 0) {
      console.warn('GraveLocator: No lots data provided or empty lots array');
    }
  }, [lots?.length]); // Only log when lots length changes

  return (
    <div className="grave-locator">
      <div className="locator-header">
        <h3>Grave Locator</h3>
        <button
          type="button"
          className={`nav-mode-btn ${navigationMode ? 'active' : ''}`}
          onClick={toggleNavigationMode}
          title={navigationMode ? "Exit Navigation Mode" : "Point-to-Point Navigation"}
        >
          <Navigation size={16} />
          {navigationMode ? 'Exit Navigation' : 'Navigate'}
        </button>
      </div>

      {!navigationMode ? (
        // Standard search mode
        <>
          <form className="locator-form" onSubmit={e => e.preventDefault()}>
            <input
              type="text"
              placeholder="Search grave ID, row, column, or type (e.g., 'Grave 1-1', '1-1', 'grave 2')"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            {query && (
              <button
                type="button"
                className="clear-btn"
                aria-label="Clear search"
                onClick={handleClear}
              >
                ×
              </button>
            )}
            <button
              type="submit"
              className="search-btn"
              aria-label="Search"
            >
              <SearchIcon />
            </button>
          </form>

          {/* Show results or no results message */}
          {query && !isNavigating && (
            <div className="search-feedback">
              {results.length > 0 ? (
                <ul className="locator-results">
                  {results.map((lot, index) => (
                    <li key={lot._id || lot.id || index}>
                      <div className="lot-info">
                        <strong>{lot.name || lot.id || lot._id || `Lot ${index + 1}`}</strong>
                        {lot.landmark && (
                          <div className="lot-landmark">📍 {lot.landmark}</div>
                        )}
                      </div>
                      <button onClick={() => {
                        console.log('GraveLocator: Selected lot for navigation:', lot);
                        // Clear search results and start navigation
                        setQuery('');
                        setResults([]);
                        // Trigger navigation to this grave
                        if (onSelect) {
                          onSelect(lot);
                        }
                      }}>
                        Navigate to this grave
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="no-results">
                  <p>No results found for "{query}"</p>
                  <small>Try searching for: grave ID, row/column numbers, or type</small>
                </div>
              )}
            </div>
          )}

          {/* Navigation Content - replaces search results when navigating */}
          {isNavigating && navigationRoute && (
            <div className="locator-navigation">
              <div className="nav-instruction">
                <div className="nav-icon">
                  {getNavigationIcon(
                    navigationRoute.instructions[currentStep]?.instruction,
                    navigationRoute.instructions[currentStep]?.type
                  )}
                </div>
                <div className="nav-text">
                  <div className="nav-main">
                    {getEnhancedInstruction(
                      navigationRoute.instructions[currentStep]?.instruction,
                      navigationRoute.instructions[currentStep]?.type
                    )}
                  </div>
                  <div className="nav-distance">
                    {navigationRoute.instructions[currentStep]?.distance > 0 
                      ? `${Math.round(navigationRoute.instructions[currentStep].distance)}m`
                      : ''
                    }
                  </div>
                </div>
              </div>

              <div className="nav-progress">
                <div className="nav-steps">
                  {navigationRoute.instructions.map((step, index) => (
                    <div 
                      key={index}
                      className={`nav-step ${index === currentStep ? 'active' : index < currentStep ? 'completed' : ''}`}
                    >
                      <span className="step-icon">
                        {getNavigationIcon(step.instruction, step.type)}
                      </span>
                      <span className="step-text">
                        {getEnhancedInstruction(step.instruction, step.type)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="nav-actions">
                <button 
                  className="nav-btn secondary" 
                  onClick={onStopNavigation}
                >
                  End Navigation
                </button>
                <button 
                  className="nav-btn primary" 
                  onClick={onNextStep}
                  disabled={currentStep >= navigationRoute.instructions.length - 1}
                >
                  {currentStep >= navigationRoute.instructions.length - 1 ? 'Arrived' : 'Next'}
                </button>
              </div>

              <div className="nav-info">
                <div className="nav-distance-total">
                  <Ruler size={14} />
                  {Math.round(navigationRoute.totalDistance)}m
                </div>
                <div className="nav-time">
                  <Clock size={14} />
                  {navigationRoute.estimatedTime} min
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        // Navigation mode
        <div className="navigation-mode">
          {!startingLot ? (
            // Step 1: Select starting lot
            <>
              <div className="navigation-step">
                <div className="step-header">
                  <MapPin size={16} />
                  <span>Step 1: Select Starting Location</span>
                </div>
                <form className="locator-form" onSubmit={e => e.preventDefault()}>
                  <input
                    type="text"
                    placeholder="Search starting lot (grave ID, row, column, or type)"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                  />
                  {query && (
                    <button
                      type="button"
                      className="clear-btn"
                      aria-label="Clear search"
                      onClick={handleClear}
                    >
                      ×
                    </button>
                  )}
                  <button
                    type="submit"
                    className="search-btn"
                    aria-label="Search"
                  >
                    <SearchIcon />
                  </button>
                </form>

                {results.length > 0 && (
                  <ul className="locator-results">
                    {results.map(lot => (
                      <li key={lot._id}>
                        <div className="lot-info">
                          <strong>{lot.name || lot.id}</strong>
                          {lot.landmark && (
                            <div className="lot-landmark">📍 {lot.landmark}</div>
                          )}
                        </div>
                        <button onClick={() => handleSetStartingLot(lot)}>
                          Set as Start
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          ) : (
            // Step 2: Select destination lot
            <>
              <div className="navigation-step completed">
                <div className="step-header">
                  <MapPin size={16} />
                  <span>Starting Location</span>
                  <button
                    type="button"
                    className="reset-btn"
                    onClick={resetNavigation}
                    title="Change starting location"
                  >
                    <X size={14} />
                  </button>
                </div>
                <div className="selected-lot">
                  <strong>{startingLot.id}</strong> – {startingLot.name || '—'}
                  {startingLot.landmark && (
                    <div className="lot-landmark">📍 {startingLot.landmark}</div>
                  )}
                </div>
              </div>

              <div className="navigation-step">
                <div className="step-header">
                  <Navigation size={16} />
                  <span>Step 2: Select Destination</span>
                </div>
                <form className="locator-form" onSubmit={e => e.preventDefault()}>
                  <input
                    type="text"
                    placeholder="Search destination lot (grave ID, row, column, or type)"
                    value={destinationQuery}
                    onChange={e => setDestinationQuery(e.target.value)}
                  />
                  {destinationQuery && (
                    <button
                      type="button"
                      className="clear-btn"
                      aria-label="Clear search"
                      onClick={handleDestinationClear}
                    >
                      ×
                    </button>
                  )}
                  <button
                    type="submit"
                    className="search-btn"
                    aria-label="Search"
                  >
                    <SearchIcon />
                  </button>
                </form>

                {destinationResults.length > 0 && (
                  <ul className="locator-results">
                    {destinationResults.map(lot => (
                      <li key={lot._id}>
                        <div className="lot-info">
                          <strong>{lot.name || lot.id}</strong>
                          {lot.landmark && (
                            <div className="lot-landmark">📍 {lot.landmark}</div>
                          )}
                        </div>
                        <button 
                          className="navigate-btn"
                          onClick={() => handleNavigateToDestination(lot)}
                        >
                          Navigate Here
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
